import * as turf from '@turf/turf';
import type { BBox } from 'geojson';
import { IPoint, ITransform } from './types';

export function findThirdPoint(pointA: Point, pointB: Point, distance: number): Point {
  const vectorAB = [pointB.x - pointA.x, pointB.y - pointA.y];
  const magAB = Math.sqrt(vectorAB[0] ** 2 + vectorAB[1] ** 2);
  const normAB = [vectorAB[0] / magAB, vectorAB[1] / magAB];
  const scaledVector = [normAB[0] * distance, normAB[1] * distance];
  const pointC: Point = new Point(pointB.x + scaledVector[0], pointB.y + scaledVector[1]);
  return pointC;
}

export function calculateAngleBetweenPoints(pointA: Point, pointB: Point, pointC: Point): number {
  const vectorAB = [pointB.x - pointA.x, pointB.y - pointA.y];
  const vectorBC = [pointC.x - pointB.x, pointC.y - pointB.y];

  const angleAB = Math.atan2(vectorAB[1], vectorAB[0]);
  const angleBC = Math.atan2(vectorBC[1], vectorBC[0]);

  const angleRad = angleBC - angleAB;
  let angleDeg = turf.radiansToDegrees(angleRad);

  if (angleDeg < 0) {
    angleDeg += 360;
  }

  return angleDeg - 180;
}

interface CoordinatesResult {
  coordinates: IPoint[];
  latDegPerFoot: number;
  lngDegPerFoot: number;
  latDiff: number;
  lngDiff: number;
}

export function calculateCoordinates(
  centerLng: number,
  centerLat: number,
  lengthFeet: number,
  widthFeet: number,
  rotation: number = 0
): CoordinatesResult {
  const latDegPerFoot = 1 / 364000;
  const lngDegPerFoot = 1 / (364000 * Math.cos((centerLat * Math.PI) / 180));

  const latDiff = (lengthFeet / 2) * latDegPerFoot;
  const lngDiff = (widthFeet / 2) * lngDegPerFoot;

  const points = [
    { x: centerLng - lngDiff, y: centerLat + latDiff },
    { x: centerLng + lngDiff, y: centerLat + latDiff },
    { x: centerLng + lngDiff, y: centerLat - latDiff },
    { x: centerLng + -lngDiff, y: centerLat - latDiff },
  ];

  // BBox format: [west, south, east, north, minHeight?, maxHeight?]
  const bbox: BBox = [points[0].x, points[0].y, points[2].x, points[2].y];
  const bboxPolygon = turf.bboxPolygon(bbox);
  const rotatedPolygon = turf.transformRotate(bboxPolygon, rotation);
  const rotatedCoords = turf.getCoords(rotatedPolygon)[0].slice(0, 4);

  return {
    coordinates: rotatedCoords.map((point: [number, number]) => new Point(point[0], point[1])),
    latDegPerFoot,
    lngDegPerFoot,
    latDiff,
    lngDiff,
  };
}

export class Point implements IPoint {
  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  isZero(): boolean {
    return this.x === 0 && this.y === 0;
  }

  toLngLat(): [number, number] {
    return [this.x, this.y];
  }
}

export class Transform implements ITransform {
  rotation: number;
  private _offset: Point;

  constructor() {
    this.rotation = 0;
    this._offset = new Point(0, 0);
  }

  set offset(value: Point) {
    this._offset = value;
  }

  get offset(): Point {
    return this._offset;
  }
}
